export type Intent =
  | 'income_now'
  | 'income_later'
  | 'accumulation'
  | 'principal_protection'
  | 'liquidity';

export type MvaPref = 'yes' | 'no' | 'either';
export type TaxPref = 'q' | 'nq' | 'either';
export type RatingFloor = 'A++' | 'A+' | 'A' | 'A-' | null;

/** Denormalized active-case view used by the case bar and broker pages.
 * Backed by Calculator V2 tables: v2_cases (scenario) + v2_clients (person).
 * The service flattens the join so consumers don't deal with two records. */
export interface Case {
  id: number;             // v2_cases.id
  caseCode: string;       // v2_cases.case_code (BN-YYYY-MM-DD-XXXX)
  clientId: number | null;// v2_cases.client_id (null = exploration)
  mode: 'bound' | 'exploration';
  stage: string;          // v2_cases.stage — last stage broker was on
  label: string | null;   // persona display name when client-less; null for bound

  // Client (from v2_clients via join; null for exploration)
  name: string;           // bound → client name; persona → label; else 'Untitled persona'
  age: number | null;
  state: string | null;
  spouseAge: number | null;
  gender: 'M' | 'F' | null;
  maritalStatus: string | null;

  // Scenario (from v2_cases)
  premium: number | null;
  paymentMode: 'contribution' | 'income' | null;
  paymentFrequency: 'annual' | 'monthly' | null;
  deferralPeriod: number | null;
  premiumType: 'lump' | 'periodic' | null;
  termMin: number | null;
  termMax: number | null;
  ratingFloor: RatingFloor;
  mvaPref: MvaPref | null;
  taxPref: TaxPref | null;          // mapped from v2_cases.tax_status
  riderType: string | null;
  intents: Intent[];                // parsed from v2_cases.intents CSV

  createdAt: string;
  lastModifiedAt: string;
  lastOpenedAt: string;              // alias of updated_at for now
}

/** Quick-create payload from the slim "+ New case" modal (4 fields). */
export interface CaseInput {
  name: string;            // typed as one string; service splits on first space
  age?: number | null;
  state?: string | null;
  premium?: number | null;
  // Optional scenario fields, used when editing on the detail page.
  termMin?: number | null;
  termMax?: number | null;
  mvaPref?: MvaPref | null;
  taxPref?: TaxPref | null;
  ratingFloor?: RatingFloor;
  intents?: Intent[];
  paymentMode?: 'contribution' | 'income' | null;
  paymentFrequency?: 'annual' | 'monthly' | null;
  deferralPeriod?: number | null;
  premiumType?: 'lump' | 'periodic' | null;
  riderType?: string | null;
  label?: string | null;   // persona display name (client-less cases)
}

/** Tier 1 — applied as hard filters by default. */
export interface Tier1Filters {
  age: number | null;
  state: string | null;
  premium: number | null;
}

/** Tier 2 — used for ranking / highlighting, never to hide. */
export interface Tier2Prefs {
  termMin: number | null;
  termMax: number | null;
  mvaPref: MvaPref | null;
  taxPref: TaxPref | null;
  ratingFloor: RatingFloor;
}

export const ALL_INTENTS: Intent[] = [
  'income_now',
  'income_later',
  'accumulation',
  'principal_protection',
  'liquidity',
];

export const INTENT_LABELS: Record<Intent, string> = {
  income_now: 'Income now',
  income_later: 'Income later',
  accumulation: 'Accumulation',
  principal_protection: 'Principal protection',
  liquidity: 'Liquidity',
};

export interface CaseTouchDiff {
  newIds: number[];
  droppedIds: number[];
}

/** A product saved to a case's candidate shortlist (dbo.v2_case_products).
 *  The persisted client shortlist — distinct from the transient compare Basket. */
export interface CaseCandidate {
  id: number;
  caseId: number;
  productId: number;
  productType: string;        // fa | ia | va | rila
  name: string | null;
  carrier: string | null;
  source: string | null;      // where it was added from
  fit: boolean | null;        // fit vs the case at add time
  addedAt: string;
}

/** Payload for adding a product to a case's candidate shortlist. */
export interface CaseCandidateInput {
  productId: number;
  productType: string;
  name?: string | null;
  carrier?: string | null;
  source?: string | null;
  fit?: boolean | null;
}
